import React from 'react';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import {
  NudgeFastTruckIcon,
  NudgeLocationPinIcon,
  NudgeCartIcon,
  NudgeGemIcon,
  NudgeBoxIcon,
  NudgeExploreIcon,
  NudgeDownloadIcon,
} from '@reverbdotcom/cadence/icons/react';
import { RCNudgeProps, RCEyebrowProps, ShippingPromoProps } from '@reverbdotcom/cadence/components';
import {
  core_apimessages_Listing_ListingType as ListingType,
  core_apimessages_Country as Country,
  ListingForBuyerFields,
  ListingForBuyerShippingFields,
  core_apimessages_ShippingMethod as IShippingMethod,
  core_apimessages_ShippingPrice as IShippingPrice,
  core_apimessages_Money as IMoney,
  InOtherCartsCardData,
} from './rql'; 
import { LISTING_STATES } from './constants';
import { conditionDisplayText } from './components/condition_display';
import { ListingGreatValueFragment } from './listing_great_value';
import { shouldShowBumpTag } from './components/bump_tag';

export enum PriceNudge {
  DIGITAL_DOWNLOAD = 'digitalDownload',
  FREE_EXPEDITED_SHIPPING = 'freeExpeditedShipping',
  FREE_SHIPPING = 'freeShipping',
}
interface NudgeConfig {
  returnPolicyDays: number;
  inOtherCartsCount: number;
  displayMultiple?: boolean;
  greatValue?: boolean;
  localPickupOnly?: boolean;
  localPickupAvailable?: boolean;
  newListing?: boolean;
  country?: Country;
}

interface PriceNudgeConfig {
  freeExpeditedShippingAvailable?: boolean;
  freeStandardShippingAvailable?: boolean;
  digitalDownload?: boolean;
}

export function getEyebrow(listing: ListingForBuyerFields.Fragment, showBump = true): RCEyebrowProps | undefined {
  const priceDrop = !!listing?.pricing?.originalPrice;

  if (shouldShowBumpTag(listing) && showBump) {
    return { text: I18n.t('commons.listingCardEyebrows.bump'), type: 'highlight' };
  }

  if (priceDrop) {
    return { text: I18n.t('commons.listingCardEyebrows.priceDrop'), type: 'alert' };
  }
}

export const getListingCardNudgesWithLabels = (
  inOtherCartsCount = 0,
  returnPolicyDays = 0,
  country: Country = {},
): { [k:string] : RCNudgeProps } => ({
  digitalDownload: {
    label: I18n.t('commons.listingCardNudge.digitalDownload'),
    icon: () => <NudgeDownloadIcon />,
  },
  freeExpeditedShipping: {
    label: I18n.t('commons.listingCardNudge.freeExpeditedShipping'),
    icon: () => <NudgeFastTruckIcon />,
  },
  freeShipping: {
    label: I18n.t('commons.listingCardNudge.freeShipping'),
    icon: () => <NudgeFastTruckIcon />,
  },
  greatValue: {
    label: I18n.t('commons.listingCardNudge.greatValue'),
    icon: () => <NudgeGemIcon />,
  },
  inOtherCarts: {
    label: I18n.t('commons.listingCardNudge.inOtherCarts', { count: (inOtherCartsCount) }),
    icon: () => <NudgeCartIcon />,
  },
  localPickup: {
    label: I18n.t('commons.listingCardNudge.localPickup'),
    icon: () => <NudgeLocationPinIcon />,
  },
  localPickupAvailable: {
    label: I18n.t('commons.listingCardNudge.localPickupAvailable'),
    icon: () => <NudgeLocationPinIcon />,
  },
  returnPolicy: {
    label: I18n.t('commons.listingCardNudge.returnPolicy', { returnPolicyDays: (returnPolicyDays) }), icon: () => <NudgeBoxIcon />,
  },
  newListing: {
    label: I18n.t('commons.listingCardNudge.newListing'),
    icon: () => <NudgeExploreIcon />,
  },
  countryFlag: {
    label: country.name,
    icon: () => (
      <span className="listing-shop-country-nudge">
        <span className={`flag-icon flag-icon-${country?.countryCode.toLowerCase()}`} />
      </span>
    ),
  },
});

export function getA11yPriceDescription(decoratedListing: ListingForBuyer): string {
  const { primaryPriceDisplay, discountedPriceDisplay, discountPriceDescription } = decoratedListing;

  if (discountedPriceDisplay) {
    const args = { originalPrice: discountedPriceDisplay, buyerPrice: primaryPriceDisplay };
    if (discountPriceDescription) {
      return I18n.t('commons.a11yPriceDescription.withRibbon', { ...args, ribbon: discountPriceDescription });
    } else {
      return I18n.t('commons.a11yPriceDescription.withoutRibbon', args);
    }
  }

  return primaryPriceDisplay;
}

export function getNudges(config: NudgeConfig): RCNudgeProps[] {
  const listingCardNudges = getListingCardNudgesWithLabels(config.inOtherCartsCount, config.returnPolicyDays, config.country);
  const applicableNudges = [];
  const requiredNudges = [];

  if (config.country) { requiredNudges.push(listingCardNudges.countryFlag); }
  if (config.returnPolicyDays >= 14) { applicableNudges.push(listingCardNudges.returnPolicy); }
  if (config.inOtherCartsCount > 0) { applicableNudges.push(listingCardNudges.inOtherCarts); }
  if (config.localPickupOnly) { applicableNudges.push(listingCardNudges.localPickup); }
  if (config.localPickupAvailable) { applicableNudges.push(listingCardNudges.localPickupAvailable); }
  if (config.greatValue) { applicableNudges.push(listingCardNudges.greatValue); }
  if (config.newListing) { applicableNudges.push(listingCardNudges.newListing); }

  const slicedApplicableNudges = applicableNudges.slice(0, config.displayMultiple ? 3 : 1);
  
  return [...requiredNudges, ...slicedApplicableNudges];
}

function getPriceNudge(config: PriceNudgeConfig): PriceNudge {
  if (config.digitalDownload) { return PriceNudge.DIGITAL_DOWNLOAD;}
  if (config.freeExpeditedShippingAvailable) { return PriceNudge.FREE_EXPEDITED_SHIPPING; }
  if (config.freeStandardShippingAvailable) { return PriceNudge.FREE_SHIPPING; }

  return null;
}

export function getPriceNudgeProps(nudge: PriceNudge): RCNudgeProps {
  const listingCardNudges = getListingCardNudgesWithLabels();

  switch (nudge) {
    case PriceNudge.DIGITAL_DOWNLOAD:
      return listingCardNudges.digitalDownload;
    case PriceNudge.FREE_EXPEDITED_SHIPPING:
      return listingCardNudges.freeExpeditedShipping;
    case PriceNudge.FREE_SHIPPING:
      return listingCardNudges.freeShipping;
    default:
      return undefined;
  }
}

export function shippingPriceDataForMethod(
  listing: ListingForBuyerFields.Fragment, 
  shippingMethod: IShippingMethod,
): ListingForBuyerShippingFields.ShippingPrices {
  const shippingMethodConfigs = listing?.shipping?.shippingPrices || [];

  return shippingMethodConfigs.find((price: IShippingPrice) => {
    return price.shippingMethod === shippingMethod;
  });
}

export function getStandardShippingPrice(listing: ListingForBuyerFields.Fragment): IMoney | undefined {
  return shippingPriceDataForMethod(listing, IShippingMethod.SHIPPED)?.rate;
}

export function getCombinedShippingPrice(listing: ListingForBuyerFields.Fragment): IMoney | undefined {
  return shippingPriceDataForMethod(listing, IShippingMethod.COMBINED_SHIPPING)?.rate;
}

export function getExpeditedShippingPrice(listing: ListingForBuyerFields.Fragment): IMoney | undefined {
  return shippingPriceDataForMethod(listing, IShippingMethod.EXPEDITED_SHIPPING)?.rate;
}

export interface ListingForBuyer {
  isLive: boolean,
  conditionLabel: string,
  isDigital: boolean,
  isAuction: boolean,

  // Pricing
  primaryPriceDisplay: string;
  discountedPriceDisplay: string;
  discountPriceDescription: string;
  priceNudge:PriceNudge; 

  // Shipping
  localPickupOnly: boolean;
  listingShipsToBuyerRegion: boolean;
  freeExpeditedShippingAvailable: boolean;
  freeStandardShippingAvailable: boolean;
  shippingThresholdMet: boolean;
  standardShippingPrice: IMoney | undefined;
  expeditedShippingPrice: IMoney | undefined;
  combinedShippingPrice: IMoney | undefined;
  shippingDependsOnPostalCode: boolean;
  localPickupAvailable: boolean;
  shippingPromo: ShippingPromoProps | undefined;
  primaryShippingDisplay: string;
}

export function buildListingForBuyer(
  listing: ListingForBuyerFields.Fragment & InOtherCartsCardData.Fragment,
  combinedDiscountActive = false,
  shippingThresholdMet = false,
): ListingForBuyer {
  const isDigital = listing?.listingType === ListingType.DIGITAL;
  const isAuction = listing?.listingType === ListingType.AUCTION;
  const conditionLabel = !isDigital && listing?.condition
    ? conditionDisplayText(listing?.condition?.conditionSlug, listing?.condition?.displayName)
    : undefined;

  // Shipping
  const standardShippingData = shippingPriceDataForMethod(listing, IShippingMethod.SHIPPED);
  const expeditedShippingData = shippingPriceDataForMethod(listing, IShippingMethod.EXPEDITED_SHIPPING);

  const { localPickupOnly, localPickup } = listing?.shipping ?? {};
  const listingShipsToBuyerRegion = !!standardShippingData;
  const freeExpeditedShippingAvailable = !!listing?.shipping?.freeExpeditedShipping;
  const freeStandardShippingAvailable = getStandardShippingPrice(listing)?.amountCents === 0;
  const freeShippingAvailable = freeExpeditedShippingAvailable || freeStandardShippingAvailable;
  const canShippingBeDiscounted = !freeShippingAvailable && !localPickupOnly;
  const shippingDependsOnPostalCode = standardShippingData?.carrierCalculated || standardShippingData?.regional || !!expeditedShippingData?.regional;

  let shippingPromo;
  if (shippingThresholdMet) {
    shippingPromo = {
      label: I18n.t('commons.freeShipping'),
      tooltipText: I18n.t('commons.freeBecauseThreshold'),
      type: 'plain',
    };
  } else if (canShippingBeDiscounted && combinedDiscountActive && !!getCombinedShippingPrice(listing)) {
    shippingPromo = {
      label: I18n.t('commons.whenYouCombineShippingLabel', { displayPrice: getCombinedShippingPrice(listing).display }),
      tooltipText: I18n.t('commons.howToCombinedShippingTooltip'),
      type: 'highlight',
    };
  }

  let primaryShippingDisplay = I18n.t('commons.plusShipping');
  if (localPickupOnly) {
    primaryShippingDisplay = null;
  } else if (freeExpeditedShippingAvailable) {
    primaryShippingDisplay = I18n.t('commons.plusFreeExpedited');
  } else if (getStandardShippingPrice(listing)) {
    primaryShippingDisplay = I18n.t('commons.plusShippingPrice', { displayPrice: getStandardShippingPrice(listing).display });
  }

  return {
    isLive: listing?.state === LISTING_STATES.LIVE,
    isDigital,
    isAuction,
    conditionLabel,
    // Pricing
    primaryPriceDisplay: listing?.pricing?.buyerPrice?.display,
    discountedPriceDisplay: listing?.pricing?.originalPrice?.display,
    discountPriceDescription: listing?.pricing?.ribbon?.display,
    priceNudge: getPriceNudge({ freeExpeditedShippingAvailable, freeStandardShippingAvailable, digitalDownload: isDigital }),
    // Shipping
    localPickupOnly,
    listingShipsToBuyerRegion,
    freeExpeditedShippingAvailable,
    freeStandardShippingAvailable,
    shippingThresholdMet,
    standardShippingPrice: getStandardShippingPrice(listing),
    expeditedShippingPrice: getExpeditedShippingPrice(listing),
    combinedShippingPrice: getCombinedShippingPrice(listing),
    shippingDependsOnPostalCode,
    localPickupAvailable: localPickup,
    primaryShippingDisplay,
    shippingPromo,
  };
}

export const ListingForBuyerShippingFieldsFragment = gql`
  fragment ListingForBuyerShippingFields on Listing {
    _id
    shipping {
      freeExpeditedShipping
      localPickupOnly
      shippingPrices {
        shippingMethod
        carrierCalculated
        regional
        destinationPostalCodeNeeded
        rate {
          amount
          amountCents
          currency
          display
        }
      }
    }
  }
`;

export const ListingForBuyerFragment = gql`
  fragment ListingForBuyerFields on Listing {
    _id
    id
    title
    slug
    listingType
    make
    model
    upc
    state
    stateDescription
    bumped
    watching
    soldAsIs
    publishedAt {
      seconds
    }
    condition {
      displayName
      conditionSlug
      conditionUuid
    }
    pricing {
      buyerPrice {
        display
        currency
        amount
        amountCents
      }
      originalPrice {
        display
      }
      ribbon {
        display
      }
    }
    images(input: {
      transform: "card_square",
      count: 3,
      scope: "photos",
      type: "Product"
    }) {
      source
    }
    shipping {
      shippingPrices {
        shippingMethod
        carrierCalculated
        destinationPostalCodeNeeded
        rate {
          amount
          amountCents
          currency
          display
        }
      }
      freeExpeditedShipping
      localPickupOnly
      localPickup
    }
    shop {
      _id
      returnPolicy {
        usedReturnWindowDays
        newReturnWindowDays
      }
      address {
        locality
        region
        country {
          countryCode
          name
        }
      }
    }
    ...ListingForBuyerShippingFields
    ...ListingGreatValueData
  }
  ${ListingGreatValueFragment}
  ${ListingForBuyerShippingFieldsFragment}
`;
